import { getReduceMotion, onMotionPreferenceChange } from './motion.ts';
import { startCountdownIfNeeded } from './countdown.ts';
import { trackSlideView, trackScroll, trackTimeOnSlide, trackEvent } from './analytics.ts';
import {
  addScrollListener,
  getScrollTop,
  scrollToElement,
  setScrollTop,
  getScrollContainer,
} from './scrollContainer.ts';
import type {
  SlideChangeListener,
  TopReturnListener,
  RedBannerEventListener,
  SlideNavigationOptions,
  SlideChangePayload,
} from './types.ts';

const slideChangeListeners = new Set<SlideChangeListener>();
const topReturnListeners = new Set<TopReturnListener>();
const redBannerListeners = new Set<RedBannerEventListener>();

let reduceMotion: boolean = getReduceMotion();
let navigationBlocked: boolean = false;
let historySetup: boolean = false;
let ignoreHistoryUpdate: boolean = false;
let lastHistoryIndex: number | null = null;

onMotionPreferenceChange((value: boolean) => {
  reduceMotion = value;
});

function getBannerSlide(): HTMLElement | null {
  return document.getElementById('special-offer');
}

function getRedBannerSlide(): HTMLElement | null {
  return document.getElementById('slow-down');
}

function getRedBanner(): HTMLElement | null {
  return document.getElementById('redBanner');
}

function getFreeMaterialsSlide(): HTMLElement | null {
  return document.getElementById('free-materials');
}

function getDataTrailSection(): HTMLElement | null {
  return document.getElementById('data');
}

function getSlideHash(index: number): string {
  const id = slideIdByIndex[index];
  return id ? `#${id}` : '';
}

function setupStaticScrollIndicators(): void {
  const heroIndicator = document.querySelector('#intro .scroll-indicator');
  const arrowMarkup =
    heroIndicator?.querySelector('svg')?.outerHTML ||
    '<svg class="scroll-indicator__arrow" viewBox="0 0 32 32" aria-hidden="true"><path d="M5.2 10.4 L16 24 L26.8 10.4" fill="none" stroke="currentColor" stroke-width="2.0" stroke-linecap="round" stroke-linejoin="round" /></svg>';
  const ariaLabel = heroIndicator?.getAttribute('aria-label') || 'Scroll to next section';

  const excludedIds = new Set(['intro', 'slow-down', 'cta2']);

  const slides = Array.from(document.querySelectorAll('.slide'));

  slides.forEach((slide) => {
    const id = slide.getAttribute('id') || '';
    if (excludedIds.has(id)) return;
    if (slide.querySelector('.scroll-indicator')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'scroll-indicator scroll-indicator--static';
    button.setAttribute('aria-label', ariaLabel);
    button.innerHTML = arrowMarkup;

    button.addEventListener('click', () => {
      const currentSlides = slideController.slides;
      const targetIndex = currentSlides.indexOf(slide as HTMLElement);
      if (targetIndex === -1) {
        return;
      }
      const nextIndex = Math.min(currentSlides.length - 1, targetIndex + 1);
      if (nextIndex === targetIndex) {
        return;
      }
      slideController.goTo(nextIndex, { source: 'static-scroll-indicator' });
    });

    slide.appendChild(button);
  });
}

function getRedBannerRect(): DOMRect | null {
  const slide = getRedBannerSlide();
  return slide?.getBoundingClientRect?.() || null;
}

class SlideController {
  slides: HTMLElement[];
  index: number;
  locked: boolean;
  bannerIndex: number;
  redBannerIndex: number;
  activeTransition: Promise<void> | null;
  unlockTimer: number | null;
  redBannerShown: boolean;
  freeMaterialsIndex: number;
  dataTrailIndex: number;
  ignoreObserver: boolean;
  observer: IntersectionObserver | null;
  lastNavigationSource: string;

  constructor() {
    this.slides = [];
    this.index = 0;
    this.locked = false;
    this.bannerIndex = -1;
    this.redBannerIndex = -1;
    this.activeTransition = Promise.resolve();
    this.unlockTimer = null;
    this.redBannerShown = false;
    this.freeMaterialsIndex = -1;
    this.dataTrailIndex = -1;
    this.ignoreObserver = false;
    this.refresh();
    this.observer = null;
    this.setupObserver();
    this.updateRedBanner();
    this.lastNavigationSource = 'generic';
  }

  refresh(): void {
    this.slides = Array.from(document.querySelectorAll<HTMLElement>('.slide'));
    const bannerSlide = getBannerSlide();
    this.bannerIndex = bannerSlide ? this.slides.indexOf(bannerSlide) : -1;
    const redBannerSlide = getRedBannerSlide();
    this.redBannerIndex = redBannerSlide ? this.slides.indexOf(redBannerSlide) : -1;
    const freeMaterialsSlide = getFreeMaterialsSlide();
    this.freeMaterialsIndex = freeMaterialsSlide ? this.slides.indexOf(freeMaterialsSlide) : -1;
    const dataTrailSection = getDataTrailSection();
    this.dataTrailIndex = dataTrailSection ? this.slides.indexOf(dataTrailSection) : -1;
  }

  setupObserver(): void {
    if (!('IntersectionObserver' in window)) return;

    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.ignoreObserver) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = this.slides.indexOf(entry.target as HTMLElement);
            if (idx !== -1) this.handleIndexChange(idx);
          }
        });
      },
      { threshold: 0.5 }
    );

    this.slides.forEach((slide: HTMLElement, idx: number) => {
      if (idx > 0) this.observer?.observe(slide);
    });
  }

  get current(): HTMLElement | null {
    return this.slides[this.index] || null;
  }

  get nextIndex(): number {
    return Math.min(this.slides.length - 1, this.index + 1);
  }

  get prevIndex(): number {
    return Math.max(0, this.index - 1);
  }

  goTo(index: number, { source = 'generic' }: SlideNavigationOptions = {}): Promise<void> {
    if (navigationBlocked) return this.activeTransition || Promise.resolve();
    if (this.locked) return this.activeTransition || Promise.resolve();
    if (index < 0 || index >= this.slides.length) return this.activeTransition || Promise.resolve();

    const target = this.slides[index];
    if (!target) return this.activeTransition || Promise.resolve();

    this.locked = true;
    this.ignoreObserver = true;
    this.lastNavigationSource = source;

    const run = async () => {
      const behavior = reduceMotion ? 'auto' : 'smooth';

      scrollToElement(target, behavior as ScrollBehavior);

      this.handleIndexChange(index);

      if (this.unlockTimer) {
        clearTimeout(this.unlockTimer);
      }
      // Standard timing for all navigation
      let delay = reduceMotion ? 350 : 700;
      this.unlockTimer = window.setTimeout(() => {
        this.locked = false;
        this.unlockTimer = null;
        this.ignoreObserver = false;
      }, delay);
    };

    this.activeTransition = (this.activeTransition || Promise.resolve()).then(run).catch(() => {
      this.locked = false;
      this.ignoreObserver = false;
    });

    return this.activeTransition;
  }

  goNext(options?: SlideNavigationOptions): Promise<void> {
    return this.goTo(this.nextIndex, options);
  }

  goPrev(options?: SlideNavigationOptions): Promise<void> {
    return this.goTo(this.prevIndex, options);
  }

  handleIndexChange(index: number): void {
    // Track time on previous slide
    if (this.index !== index) {
      const prevSlideId = slideIdByIndex[this.index];
      const duration = (Date.now() - slideStartTime) / 1000;
      if (prevSlideId) {
        trackTimeOnSlide(prevSlideId, duration);
      }
      slideStartTime = Date.now();
    }

    this.index = index;
    const slideId = slideIdByIndex[index];
    if (!slideId || slideId === null) return;
    trackSlideView(slideId, index);
    notifySlideChange(index);
    this.updateRedBanner();
    trackTopReturn(index);
  }

  updateRedBanner(forceShow = false) {
    const redBanner = getRedBanner();
    if (!redBanner) return;
    const atRedBannerSlide = this.redBannerIndex !== -1 && this.index === this.redBannerIndex;
    const shouldShow = forceShow || atRedBannerSlide;

    if (shouldShow) {
      if (!this.redBannerShown) {
        redBanner.classList.add('is-visible');
        document.body.classList.add('red-banner-visible');
        this.redBannerShown = true;
        startCountdownIfNeeded();
        notifyRedBannerEvent({ type: 'show', getRect: getRedBannerRect });
        trackEvent('red_banner_show');
      }
      if (atRedBannerSlide) {
        notifyRedBannerEvent({ type: 'at-slide', getRect: getRedBannerRect });
      } else {
        notifyRedBannerEvent({ type: 'away' });
      }
    } else if (this.redBannerShown) {
      redBanner.classList.remove('is-visible');
      document.body.classList.remove('red-banner-visible');
      this.redBannerShown = false;
      notifyRedBannerEvent({ type: 'hide' });
      trackEvent('red_banner_hide');
    }
  }
}

const slideController = new SlideController();

let highestSlideIndexReached: number = 0;
const slideIdByIndex: (string | null)[] = [];
let slideStartTime: number = Date.now();
(function buildSlideIndexMap() {
  const slides = Array.from(document.querySelectorAll('.slide'));
  slides.forEach((slide, idx) => {
    slideIdByIndex[idx] = slide.getAttribute('id') || null;
  });
})();

function notifySlideChange(index: number): void {
  const payload: SlideChangePayload = {
    index,
    slideId: slideIdByIndex[index] || '',
    previousIndex: slideController.index,
    source: slideController.lastNavigationSource,
  };
  slideChangeListeners.forEach((fn) => {
    try {
      fn(payload);
    } catch (err) {
      console.error('Slide listener failed', err);
    }
  });
}

function notifyTopReturn(): void {
  topReturnListeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.error('Top-return listener failed', err);
    }
  });
}

function notifyRedBannerEvent(event: {
  type: string;
  getRect?: () => DOMRect | null;
  visible?: boolean;
}): void {
  redBannerListeners.forEach((fn) => {
    try {
      fn(event);
    } catch (err) {
      console.error('Red banner listener failed', err);
    }
  });
}

function trackTopReturn(index: number): void {
  if (index > highestSlideIndexReached) {
    highestSlideIndexReached = index;
  }

  if (index === 0 && highestSlideIndexReached > 1) {
    notifyTopReturn();
  }
}

function furthestSlideHash() {
  const targetIndex = Math.max(0, highestSlideIndexReached);
  const id = slideIdByIndex[targetIndex];
  return id ? `#${id}` : '#whySimilar';
}

function handleKeyNav(e: KeyboardEvent): void {
  if (navigationBlocked) return;

  if (e.key === 'ArrowUp' || e.key === 'PageUp') {
    e.preventDefault();
    goToRelative(-1, { source: 'keyboard' });
  } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
    e.preventDefault();
    goToRelative(1, { source: 'keyboard' });
  } else if (e.key === 'Home') {
    e.preventDefault();
    goToSlide(0, { source: 'keyboard' });
  } else if (e.key === 'End') {
    e.preventDefault();
    goToSlide(slideController.slides.length - 1, { source: 'keyboard' });
  }
}

document.addEventListener('keydown', handleKeyNav);

function goToSlide(index: number, options?: SlideNavigationOptions): Promise<void> {
  return slideController.goTo(index, options);
}

function goToRelative(delta: number, options?: SlideNavigationOptions): Promise<void> {
  if (delta > 0) return slideController.goNext(options);
  if (delta < 0) return slideController.goPrev(options);
  return slideController.activeTransition || Promise.resolve();
}

function initSlides() {
  // Remove hash to prevent scrolling to previous slide on reload
  window.history.replaceState(null, '', window.location.pathname);
  slideController.refresh();
  slideController.setupObserver();
  setupStaticScrollIndicators();
  setupHistoryNavigation();

  const syncTopSlide = () => {
    if (navigationBlocked) return;
    if (slideController.locked) return;
    if (getScrollTop() <= 4 && slideController.index !== 0) {
      slideController.handleIndexChange(0);
    }
  };

  addScrollListener((scrollTop) => {
    if (scrollTop <= 4) {
      syncTopSlide();
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      setScrollTop(0, 'auto');
      syncTopSlide();
    }
  });

  // Track scroll depth
  addScrollListener((scrollTop) => {
    const slideId = slideIdByIndex[slideController.index];
    if (!slideId) return;
    trackScroll(scrollTop, slideId);
  });

  // Wheel input (mouse + touchpad): force one-slide-per-gesture (same as keyboard arrows).
  const container = getScrollContainer();
  if (!container) return;

  let wheelLocked = false;
  let wheelUnlockTimer: number | null = null;
  let wheelQuietTimer: number | null = null;
  let wheelUnlockEarliestAt = 0;
  let wheelStreamActive = false;

  const WHEEL_STREAM_QUIET_MS = 60;

  function maybeUnlockWheel(): void {
    if (!wheelLocked) return;
    if (wheelStreamActive) return;

    const now = performance.now();
    if (now < wheelUnlockEarliestAt) return;

    wheelLocked = false;
  }

  function markWheelStreamActive(): void {
    wheelStreamActive = true;

    if (wheelQuietTimer) {
      clearTimeout(wheelQuietTimer);
    }

    wheelQuietTimer = window.setTimeout(() => {
      wheelStreamActive = false;
      wheelQuietTimer = null;
      maybeUnlockWheel();
    }, WHEEL_STREAM_QUIET_MS);
  }

  const handleWheel = (event: WheelEvent): void => {
    event.preventDefault();

    // Always extend the quiet window on any wheel event (so a burst stays one gesture)
    markWheelStreamActive();

    if (wheelLocked) return;

    const currentIndex = slideController.index;
    const hasNext = currentIndex < slideController.slides.length - 1;
    const hasPrev = currentIndex > 0;

    let moved = false;
    if (event.deltaY > 0 && hasNext) {
      wheelLocked = true;
      moved = true;
      goToRelative(1, { source: 'mouse-wheel' });
    } else if (event.deltaY < 0 && hasPrev) {
      wheelLocked = true;
      moved = true;
      goToRelative(-1, { source: 'mouse-wheel' });
    }

    // If no movement possible (top/bottom or zero delta), do not lock.
    if (!moved) return;

    if (wheelUnlockTimer) {
      clearTimeout(wheelUnlockTimer);
    }

    const unlockDelay = reduceMotion ? 45 : 90;
    wheelUnlockEarliestAt = performance.now() + unlockDelay + 8;
    wheelUnlockTimer = window.setTimeout(() => {
      wheelUnlockTimer = null;
      maybeUnlockWheel();
    }, unlockDelay + 8);
  };

  container.addEventListener('wheel', handleWheel as EventListener, { passive: false });
}

function addSlideChangeListener(fn: SlideChangeListener): () => void {
  if (typeof fn !== 'function') return () => {};
  slideChangeListeners.add(fn);
  return () => slideChangeListeners.delete(fn);
}

function addTopReturnListener(fn: TopReturnListener): () => void {
  if (typeof fn !== 'function') return () => {};
  topReturnListeners.add(fn);
  return () => topReturnListeners.delete(fn);
}

function addRedBannerListener(fn: RedBannerEventListener): () => void {
  if (typeof fn !== 'function') return () => {};
  redBannerListeners.add(fn);
  return () => redBannerListeners.delete(fn);
}

function setNavigationBlocked(value: boolean): void {
  navigationBlocked = Boolean(value);
}

function setupHistoryNavigation() {
  if (historySetup) return;
  if (!('history' in window) || typeof window.history.pushState !== 'function') return;

  historySetup = true;
  lastHistoryIndex = slideController.index;

  const initialHash = getSlideHash(lastHistoryIndex);
  const currentState = window.history.state;
  if (!currentState || typeof currentState.slideIndex !== 'number') {
    window.history.replaceState(
      { slideIndex: lastHistoryIndex },
      '',
      initialHash || window.location.pathname
    );
  }

  window.addEventListener('popstate', (event) => {
    const targetIndex = typeof event.state?.slideIndex === 'number' ? event.state.slideIndex : null;
    if (navigationBlocked) {
      const direction =
        targetIndex !== null
          ? targetIndex < slideController.index
            ? 'back'
            : 'forward'
          : 'unknown';
      document.dispatchEvent(
        new CustomEvent('slide-navigation-blocked', {
          detail: {
            source: 'history',
            direction,
            targetIndex,
          },
        })
      );
      queueMicrotask(() => {
        window.history.go(1);
      });
      return;
    }
    if (targetIndex === null || targetIndex === slideController.index) {
      return;
    }
    ignoreHistoryUpdate = true;
    slideController.goTo(targetIndex, { source: 'history' });
  });

  addSlideChangeListener((payload) => {
    const index = payload.index;
    if (!historySetup) return;
    if (ignoreHistoryUpdate) {
      ignoreHistoryUpdate = false;
      lastHistoryIndex = index;
      return;
    }
    if (index === lastHistoryIndex) {
      return;
    }

    const hash = getSlideHash(index);
    window.history.pushState({ slideIndex: index }, '', hash || window.location.pathname);
    lastHistoryIndex = index;
  });
}

const onSlideChange = addSlideChangeListener;
const onTopReturn = addTopReturnListener;
const onRedBannerEvent = addRedBannerListener;

export {
  furthestSlideHash,
  goToRelative,
  goToSlide,
  initSlides,
  onRedBannerEvent,
  onSlideChange,
  onTopReturn,
  setNavigationBlocked,
  slideController,
};
